import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SaveIcon from "@material-ui/icons/Save";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(2.5),
    borderRadius: 18,
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.75)
  },
  helper: {
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center"
  }
}));

const defaultAnnouncement = {
  enabled: false,
  title: "",
  message: "",
  severity: "warning",
  dismissible: true,
  startsAt: "",
  endsAt: ""
};
const globalAnnouncementUpdatedEvent = "altchat:global-announcement-updated";

const toDateTimeLocalValue = value => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const offsetMs = parsed.getTimezoneOffset() * 60000;
  const local = new Date(parsed.getTime() - offsetMs);
  return local.toISOString().slice(0, 16);
};

const Announcements = () => {
  const classes = useStyles();
  const [announcement, setAnnouncement] = useState(defaultAnnouncement);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadWarning, setLoadWarning] = useState("");

  const loadAnnouncement = useCallback(async () => {
    try {
      const { data } = await api.get("/backups/announcement");
      setLoadWarning("");
      setAnnouncement({
        enabled: Boolean(data?.enabled),
        title: String(data?.title || ""),
        message: String(data?.message || ""),
        severity: String(data?.severity || "warning"),
        dismissible: data?.dismissible === undefined ? true : Boolean(data.dismissible),
        startsAt: toDateTimeLocalValue(data?.startsAt),
        endsAt: toDateTimeLocalValue(data?.endsAt)
      });
    } catch (err) {
      setLoadWarning(
        "Não foi possível carregar os avisos agora. Tente em Atualizar."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncement();
  }, [loadAnnouncement]);

  const handleChange = event => {
    const { name, value, checked, type } = event.target;
    setAnnouncement(current => ({
      ...current,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...announcement,
        startsAt: announcement.startsAt ? new Date(announcement.startsAt).toISOString() : null,
        endsAt: announcement.endsAt ? new Date(announcement.endsAt).toISOString() : null
      };
      const { data } = await api.put("/backups/announcement", payload);
      setAnnouncement({
        enabled: Boolean(data?.enabled),
        title: String(data?.title || ""),
        message: String(data?.message || ""),
        severity: String(data?.severity || "warning"),
        dismissible: data?.dismissible === undefined ? true : Boolean(data.dismissible),
        startsAt: toDateTimeLocalValue(data?.startsAt),
        endsAt: toDateTimeLocalValue(data?.endsAt)
      });
      window.dispatchEvent(new CustomEvent(globalAnnouncementUpdatedEvent));
      toast.success("Aviso global salvo com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainContainer scrollY="visible" fullHeight={false}>
      <MainHeader>
        <Title>Avisos globais</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" color="primary" onClick={loadAnnouncement} disabled={loading}>
            Atualizar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.card} variant="outlined">
        <Typography className={classes.sectionTitle}>Aviso para todas as empresas</Typography>
        <Typography className={classes.helper} style={{ marginBottom: 16 }}>
          Esse aviso aparece no topo da plataforma para todos os usuários logados.
        </Typography>
        {loadWarning ? (
          <Typography
            variant="body2"
            style={{ marginBottom: 12, color: "#b45309", fontWeight: 600 }}
          >
            {loadWarning}
          </Typography>
        ) : null}
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={Boolean(announcement.enabled)}
                  onChange={handleChange}
                  name="enabled"
                />
              }
              label="Ativo"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Severidade</InputLabel>
              <Select
                name="severity"
                value={announcement.severity}
                onChange={handleChange}
                label="Severidade"
              >
                <MenuItem value="info">Informativo</MenuItem>
                <MenuItem value="warning">Aviso</MenuItem>
                <MenuItem value="critical">Critico</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              type="datetime-local"
              label="Início (opcional)"
              name="startsAt"
              value={announcement.startsAt}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              type="datetime-local"
              label="Fim (opcional)"
              name="endsAt"
              value={announcement.endsAt}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={Boolean(announcement.dismissible)}
                  onChange={handleChange}
                  name="dismissible"
                />
              }
              label="Fechar"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              label="Título (opcional)"
              name="title"
              value={announcement.title}
              onChange={handleChange}
              placeholder="Ex.: Manutenção programada"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              label="Mensagem do aviso"
              name="message"
              value={announcement.message}
              onChange={handleChange}
              placeholder="Descreva manutenção, instabilidade ou orientações."
            />
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                Salvar aviso
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default Announcements;
